import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import JournalNavigation from '../../components/JournalNavigation';
import LockIndicator from '../../components/LockIndicator';
import JournalStartModal from '../../components/JournalStartModal';
import { getJournalStartConfig } from '../../utils/journalConstants';
import { isFieldDisabled } from '../../utils/lockingUtils';
import { getPriorityCategory, getSelectedHabits, formatHabitsForDropdown, getAllTransformationHabits, getAllPriority1Categories, getFilledHabitText } from '../../utils/habitUtils';
import confetti from 'canvas-confetti';
import './SelfAssessment.css';

const CheckIn05 = () => {
    const navigate = useNavigate();
    const [showStartModal, setShowStartModal] = useState(false);
    const [hasStarted, setHasStarted] = useState(false);
    const [formData, setFormData] = useState({
        // Decision Tree
        evaluationDate: '',
        pastTwoWeeksReflection: '',
        consistent80Percent: '',
        feelingProgress: '',
        continueHabits: [],
        addMoreHabits: '',
        setbacks: '',
        reduceSetbacks: '',

        // Commit
        commitDate: '',
        commitHabits: '',

        // My Progress - 14 days mood tracking
        day1Mood: '',
        day2Mood: '',
        day3Mood: '',
        day4Mood: '',
        day5Mood: '',
        day6Mood: '',
        day7Mood: '',
        day8Mood: '',
        day9Mood: '',
        day10Mood: '',
        day11Mood: '',
        day12Mood: '',
        day13Mood: '',
        day14Mood: '',
        progressNotes: ''
    });

    const [entryId, setEntryId] = useState(null);
    const [entryMetadata, setEntryMetadata] = useState({ lockedAt: null, startedAt: null, createdAt: null });
    const [emojiPopup, setEmojiPopup] = useState({ show: false, emoji: '', day: null, position: null });

    // Habbit dropdown 
    const [previousHabits, setPreviousHabits] = useState([]);
    const [customHabit, setCustomHabit] = useState('');
    const moodEmojis = ['😊', '😐', '☹️'];

    useEffect(() => {
        loadEntry();
        loadTransformationData();
    }, []);

    // Reload when cycle changes
    useEffect(() => {
        const handler = () => { loadEntry(); loadTransformationData(); };
        window.addEventListener('cycle-changed', handler);
        return () => window.removeEventListener('cycle-changed', handler);
    }, []);

    // Autosave
    useEffect(() => {
        const timeout = setTimeout(() => {
            if (Object.values(formData).some(val => val)) {
                saveEntry();
            }
        }, 2000);

        return () => clearTimeout(timeout);
    }, [formData]);

    const loadEntry = async () => {
        try {
            const response = await api.get('/journal', { params: { section: 'checkin-5' } });
            if (response.data.entries && response.data.entries.length > 0) {
                const entry = response.data.entries[0];
                setFormData({ ...formData, ...entry.data });
                setEntryId(entry._id);
                setEntryMetadata({
                    lockedAt: entry.lockedAt,
                    startedAt: entry.startedAt,
                    createdAt: entry.createdAt
                });
                setHasStarted(!!entry.startedAt);
            }
        } catch (error) {
            console.error('Error loading entry:', error);
        }
    };



    const loadTransformationData = async () => {
        try {
            const response = await api.get('/journal', {
                params: { section: 'checkin-4' }
            });
            if (response.data.entries && response.data.entries.length > 0) {
                const transformationData = response.data.entries[0].data;

                // Get all priority #1 categories and their habits
                const priority1Categories = getAllPriority1Categories(transformationData);
                const allPriority1Habits = [];

                priority1Categories.forEach(category => {
                    const habits = getSelectedHabits(transformationData, category.key);
                    habits.forEach((habitObj) => {
                        // Get the filled habit text using the habit label and category
                        const filledText = getFilledHabitText(habitObj.label, category.category, transformationData);

                        allPriority1Habits.push({
                            key: habitObj.key,
                            label: filledText,
                            category: category.name
                        });
                    });
                });

                setPreviousHabits(formatHabitsForDropdown(allPriority1Habits));
            }
        } catch (error) {
            console.error('Error loading transformation data:', error);
        }
    };

    const saveEntry = async (includeStartTime = false) => {
        try {
            const payload = {
                section: 'checkin-5',
                data: formData
            };

            if (includeStartTime) {
                payload.startedAt = new Date().toISOString();
            }

            if (entryId) {
                const response = await api.patch(`/journal/${entryId}`, payload);
                if (response.data.entry && response.data.entry.startedAt) {
                    setEntryMetadata(prev => ({
                        ...prev,
                        startedAt: response.data.entry.startedAt
                    }));
                }
            } else {
                const response = await api.post('/journal', payload);
                setEntryId(response.data.entry._id);
                setEntryMetadata({
                    lockedAt: response.data.entry.lockedAt,
                    startedAt: response.data.entry.startedAt,
                    createdAt: response.data.entry.createdAt
                });
            }
        } catch (error) {
            console.error('Error saving entry:', error);
        }
    };

    const handleChange = (field, value) => {
        if (!hasStarted) {
            if (!showStartModal) {
                setShowStartModal(true);
            }
            return; // Don't update form until user clicks "Begin"
        }
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const handleBegin = () => {
        setShowStartModal(false);
        setHasStarted(true);
        saveEntry(true);
    };

    const handleNotNow = () => {
        setShowStartModal(false);
        // Stay on the same page - user can try again later
    };

    const handleMoodClick = (day, mood, event) => {
        if (!hasStarted) {
            if (!showStartModal) {
                setShowStartModal(true);
            }
            return; // Don't update form until user clicks "Begin"
        }
        handleChange(`day${day}Mood`, mood);

        // Get the position of the clicked element for popup positioning
        const rect = event.target.getBoundingClientRect();
        const position = {
            x: rect.left + rect.width / 2,
            y: rect.top + rect.height / 2
        };

        // Show popup
        setEmojiPopup({
            show: true,
            emoji: mood,
            day: day,
            position: position
        });

        // Trigger confetti
        confetti({
            particleCount: 100,
            spread: 70,
            colors: ['#8B4513', '#FFD700', '#D2B48C', '#FFFFFF'], // brown, gold, tan, white
            origin: {
                x: (rect.left + rect.width / 2) / window.innerWidth,
                y: (rect.top + rect.height / 2) / window.innerHeight
            }
        });

        // Hide popup after 1.5 seconds
        setTimeout(() => {
            setEmojiPopup({ show: false, emoji: '', day: null, position: null });
        }, 1500);
    };



    // Habbit dropdown related handlers
    const handleAddHabit = (habitLabel) => {
        if (habitLabel && !formData.continueHabits.includes(habitLabel)) {
            handleChange('continueHabits', [...formData.continueHabits, habitLabel]);
        }
    };

    // Habbit dropdown related handlers
    const handleRemoveHabit = (habitLabel) => {
        handleChange('continueHabits', formData.continueHabits.filter(h => h !== habitLabel));
    };

    // Habbit dropdown related handlers
    const handleAddCustomHabit = () => {
        if (customHabit.trim() && !formData.continueHabits.includes(customHabit.trim())) {
            handleChange('continueHabits', [...formData.continueHabits, customHabit.trim()]);
            setCustomHabit('');
        }
    };
    // const isLocked = entryMetadata.lockedAt !== null;
    const isLocked = false;
    const modalConfig = getJournalStartConfig('checkin-05');


    return (
        <>
            <JournalStartModal
                isOpen={showStartModal}
                onBegin={handleBegin}
                onCancel={handleNotNow}
                title={modalConfig.title}
                message={modalConfig.message}
                estimatedTime={modalConfig.estimatedTime}
            />
            <div className="self-assessment-container">
                <LockIndicator
                    lockedAt={entryMetadata.lockedAt}
                    startedAt={entryMetadata.startedAt}
                />

                <div className="journal-page-wrapper">
                    {/* Section 1: Decision Tree */}
                    <div>
                        <div className="journal-line" style={{ marginBottom: '40px', maxWidth: '200px' }}>
                            <label style={{ fontSize: '16px', display: 'block', marginBottom: '8px', color: '#2c2416' }}>Date</label>
                            <input
                                type="date"
                                value={formData.evaluationDate}
                                onChange={(e) => handleChange('evaluationDate', e.target.value)}
                                disabled={isLocked}
                            />
                        </div>

                        <div style={{ marginBottom: '40px' }}>
                            <p className="handwritten-label" style={{
                                // fontSize: '22px',
                                color: '#2c2416', marginBottom: '20px'
                            }}>
                                The past two weeks have been
                            </p>
                            <div className="journal-line multi-line">
                                <textarea
                                    value={formData.pastTwoWeeksReflection}
                                    onChange={(e) => handleChange('pastTwoWeeksReflection', e.target.value)}
                                    placeholder=""
                                    rows={4}
                                    style={{ width: '100%', lineHeight: '32px' }}
                                    disabled={isLocked}
                                />
                            </div>
                        </div>

                        {/* Decision Tree */}
                        <div style={{ marginTop: '60px', padding: '40px 20px' }}>
                            {/* Level 1: 80% Consistency Question */}
                            <div style={{ textAlign: 'center', marginBottom: '40px' }}>
                                <p style={{ fontSize: '14px', marginBottom: '20px', color: '#2c2416' }}>
                                    Have I been at least 80% consistent with my selected health habits?
                                </p>
                                <div style={{ display: 'flex', justifyContent: 'center', gap: '60px' }}>
                                    <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                                        <input
                                            type="radio"
                                            name="consistent80Percent"
                                            value="yes"
                                            checked={formData.consistent80Percent === 'yes'}
                                            onChange={(e) => handleChange('consistent80Percent', e.target.value)}
                                            style={{ marginRight: '8px', cursor: 'pointer' }}
                                            disabled={isLocked}
                                        />
                                        <span style={{ fontSize: '15px' }}>yes</span>
                                    </label>
                                    <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                                        <input
                                            type="radio"
                                            name="consistent80Percent"
                                            value="no"
                                            checked={formData.consistent80Percent === 'no'}
                                            onChange={(e) => handleChange('consistent80Percent', e.target.value)}
                                            style={{ marginRight: '8px', cursor: 'pointer' }}
                                            disabled={isLocked}
                                        />
                                        <span style={{ fontSize: '15px' }}>no</span>
                                    </label>
                                </div>
                            </div>

                            {/* Level 2: Branch based on consistency */}
                            {formData.consistent80Percent && (
                                <div style={{ marginTop: '60px' }}>
                                    {/* Left Branch: If YES - Show progress question */}
                                    {formData.consistent80Percent === 'yes' && (
                                        <div style={{ paddingLeft: '40px', maxWidth: '600px', margin: '0 auto' }}>
                                            <p style={{ fontSize: '13px', marginBottom: '20px', color: '#2c2416', textAlign: 'center' }}>
                                                Am I feeling progress in my overall well-being?
                                            </p>
                                            <div style={{ display: 'flex', justifyContent: 'center', gap: '40px', marginBottom: '30px' }}>
                                                <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                                                    <input
                                                        type="radio"
                                                        name="feelingProgress"
                                                        value="yes"
                                                        checked={formData.feelingProgress === 'yes'}
                                                        onChange={(e) => handleChange('feelingProgress', e.target.value)}
                                                        style={{ marginRight: '8px', cursor: 'pointer' }}
                                                        disabled={isLocked}
                                                    />
                                                    <span style={{ fontSize: '14px' }}>yes</span>
                                                </label>
                                                <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                                                    <input
                                                        type="radio"
                                                        name="feelingProgress"
                                                        value="no"
                                                        checked={formData.feelingProgress === 'no'}
                                                        onChange={(e) => handleChange('feelingProgress', e.target.value)}
                                                        style={{ marginRight: '8px', cursor: 'pointer' }}
                                                        disabled={isLocked}
                                                    />
                                                    <span style={{ fontSize: '14px' }}>no</span>
                                                </label>
                                            </div>

                                            {/* Sub-branches for feeling progress */}
                                            {formData.feelingProgress && (
                                                <div style={{ fontSize: '12px', color: '#3d3425', lineHeight: '1.6', marginTop: '30px' }}>
                                                    {formData.feelingProgress === 'yes' && (
                                                        <div>
                                                            <p
                                                            // style={{ fontWeight: '600', marginBottom: '15px' }}
                                                            >I will continue with the same habits or add</p>
                                                            {/* <div className="journal-line multi-line" style={{ marginTop: '10px' }}>
                                                                <textarea
                                                                    value={formData.continueHabits}
                                                                    onChange={(e) => handleChange('continueHabits', e.target.value)}
                                                                    placeholder=""
                                                                    rows={3}
                                                                    style={{ fontSize: '12px', lineHeight: '24px' }}
                                                                    disabled={isLocked}
                                                                />
                                                            </div> */}
                                                            {/* Selected Habits List */}
                                                            {formData.continueHabits && formData.continueHabits.length > 0 && (
                                                                <div style={{
                                                                    marginBottom: '15px'
                                                                }}>
                                                                    {/* <p style={{ fontSize: '12px', fontWeight: '600', marginBottom: '10px', color: '#78716c', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                                                        Selected Habits
                                                                    </p> */}
                                                                    {formData.continueHabits.map((habit, index) => (
                                                                        <div key={index} style={{
                                                                            display: 'flex',
                                                                            justifyContent: 'space-between',
                                                                            alignItems: 'flex-start',
                                                                            padding: '8px 0',
                                                                            marginBottom: '6px',
                                                                            fontSize: '13px',
                                                                            color: '#2c2416',
                                                                            lineHeight: '1.5',
                                                                            borderBottom: '1px solid #e7e5e4'
                                                                        }}>
                                                                            <span style={{ flex: 1, paddingRight: '10px' }}>{habit}</span>
                                                                            <button
                                                                                onClick={() => handleRemoveHabit(habit)}
                                                                                disabled={isLocked}
                                                                                style={{
                                                                                    background: 'none',
                                                                                    border: 'none',
                                                                                    color: '#dc2626',
                                                                                    cursor: isLocked ? 'not-allowed' : 'pointer',
                                                                                    fontSize: '18px',
                                                                                    padding: '0',
                                                                                    width: '20px',
                                                                                    height: '20px',
                                                                                    display: 'flex',
                                                                                    alignItems: 'center',
                                                                                    justifyContent: 'center',
                                                                                    flexShrink: 0
                                                                                }}
                                                                                title="Remove habit"
                                                                            >
                                                                                ×
                                                                            </button>
                                                                        </div>
                                                                    ))}
                                                                </div>
                                                            )}

                                                            {/* Dropdown to add more habits - only show if previousHabits exist */}
                                                            {previousHabits.length > 0 && previousHabits.filter(h => !formData.continueHabits.includes(h.label)).length > 0 && (
                                                                <div style={{ marginBottom: '15px' }}>
                                                                    <select
                                                                        onChange={(e) => {
                                                                            if (e.target.value) {
                                                                                handleAddHabit(e.target.value);
                                                                                e.target.value = '';
                                                                            }
                                                                        }}
                                                                        disabled={isLocked}
                                                                        style={{
                                                                            width: '100%',
                                                                            padding: '10px 12px',
                                                                            fontSize: '13px',
                                                                            color: '#2c2416',
                                                                            cursor: 'pointer',
                                                                            backgroundColor: 'transparent',
                                                                            border: 'none',
                                                                            borderBottom: '1px solid #8b7355',
                                                                            borderRadius: '0',
                                                                            outline: 'none'
                                                                        }}
                                                                    >
                                                                        <option value="">+ Add another habit...</option>
                                                                        {previousHabits
                                                                            .filter(h => !formData.continueHabits.includes(h.label))
                                                                            .map(habit => (
                                                                                <option key={habit.value} value={habit.label}>
                                                                                    {habit.label}
                                                                                </option>
                                                                            ))
                                                                        }
                                                                    </select>
                                                                </div>
                                                            )}

                                                            {/* Other: Custom habit input - always show */}
                                                            <div style={{ marginTop: '15px' }}>
                                                                <label style={{ fontSize: '13px', fontWeight: '600', display: 'block', marginBottom: '8px', color: '#2c2416' }}>Other:</label>
                                                                <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-start' }}>
                                                                    <div className="journal-line" style={{ flex: 1 }}>
                                                                        <input
                                                                            type="text"
                                                                            value={customHabit}
                                                                            onChange={(e) => setCustomHabit(e.target.value)}
                                                                            onKeyPress={(e) => e.key === 'Enter' && handleAddCustomHabit()}
                                                                            placeholder="Type custom habit..."
                                                                            disabled={isLocked}
                                                                            style={{ fontSize: '13px' }}
                                                                        />
                                                                    </div>
                                                                    <button
                                                                        onClick={handleAddCustomHabit}
                                                                        disabled={isLocked || !customHabit.trim()}
                                                                        style={{
                                                                            padding: '10px',
                                                                            fontSize: '20px',
                                                                            fontWeight: '400',
                                                                            color: customHabit.trim() ? '#2c2416' : '#a8a29e',
                                                                            backgroundColor: 'transparent',
                                                                            border: 'none',
                                                                            borderBottom: '1px solid ' + (customHabit.trim() ? '#8b7355' : '#d6d3d1'),
                                                                            borderRadius: '0',
                                                                            cursor: (isLocked || !customHabit.trim()) ? 'not-allowed' : 'pointer',
                                                                            transition: 'all 0.2s',
                                                                            width: '45px',
                                                                            display: 'flex',
                                                                            alignItems: 'center',
                                                                            justifyContent: 'center'
                                                                        }}
                                                                    >
                                                                        +
                                                                    </button>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    )}

                                                    {formData.feelingProgress === 'no' && (
                                                        <div>
                                                            <p>I will add a couple more habits like...</p>
                                                            <div className="journal-line multi-line" style={{ marginTop: '10px' }}>
                                                                <textarea
                                                                    value={formData.addMoreHabits}
                                                                    onChange={(e) => handleChange('addMoreHabits', e.target.value)}
                                                                    placeholder=""
                                                                    rows={3}
                                                                    style={{ fontSize: '12px', lineHeight: '24px' }}
                                                                    disabled={isLocked}
                                                                />
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    )}

                                    {/* Right Branch: If NO - Show setbacks */}
                                    {formData.consistent80Percent === 'no' && (
                                        <div style={{ paddingRight: '40px', maxWidth: '600px', margin: '0 auto' }}>
                                            <p style={{ fontSize: '13px', marginBottom: '15px', color: '#2c2416', textAlign: 'center' }}>
                                                The following are setbacks that reduced my consistency
                                            </p>
                                            <div className="journal-line multi-line" style={{ marginBottom: '25px' }}>
                                                <textarea
                                                    value={formData.setbacks}
                                                    onChange={(e) => handleChange('setbacks', e.target.value)}
                                                    placeholder=""
                                                    rows={6}
                                                    style={{ fontSize: '13px', lineHeight: '32px' }}
                                                    disabled={isLocked}
                                                />
                                            </div>

                                            <p style={{ fontSize: '13px', marginBottom: '20px', color: '#2c2416', textAlign: 'center' }}>
                                                Are there ways to reduce these setbacks?
                                            </p>
                                            <div style={{ display: 'flex', justifyContent: 'center', gap: '40px', marginBottom: '20px' }}>
                                                <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                                                    <input
                                                        type="radio"
                                                        name="reduceSetbacks"
                                                        value="yes"
                                                        checked={formData.reduceSetbacks === 'yes'}
                                                        onChange={(e) => handleChange('reduceSetbacks', e.target.value)}
                                                        style={{ marginRight: '8px', cursor: 'pointer' }}
                                                        disabled={isLocked}
                                                    />
                                                    <span style={{ fontSize: '14px' }}>yes</span>
                                                </label>
                                                <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                                                    <input
                                                        type="radio"
                                                        name="reduceSetbacks"
                                                        value="no"
                                                        checked={formData.reduceSetbacks === 'no'}
                                                        onChange={(e) => handleChange('reduceSetbacks', e.target.value)}
                                                        style={{ marginRight: '8px', cursor: 'pointer' }}
                                                        disabled={isLocked}
                                                    />
                                                    <span style={{ fontSize: '14px' }}>no</span>
                                                </label>
                                            </div>

                                            {formData.reduceSetbacks && (
                                                <div style={{ fontSize: '12px', color: '#3d3425', lineHeight: '1.5', marginTop: '20px' }}>
                                                    {formData.reduceSetbacks === 'yes' && (
                                                        <div>
                                                            <p>Try again with the same habits but reduce the setbacks</p>
                                                        </div>
                                                    )}
                                                    {formData.reduceSetbacks === 'no' && (
                                                        <div>
                                                            <p>Reduce the number of habits or make the same habits easier</p>
                                                        </div>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* Bottom Message */}
                            <div style={{ marginTop: '50px', textAlign: 'center' }}>
                                <p style={{ fontSize: '13px', lineHeight: '1.7', color: '#2c2416' }}>
                                    Read your <span
                                        style={{ color: '#6366f1', cursor: 'pointer', textDecoration: 'none' }}
                                        onClick={() => navigate('/journal/self-assessment')}
                                    >
                                        motivation letter
                                    </span> again to remember what you're working towards!
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* Section 2: Commit */}
                    <div style={{ marginTop: '80px' }}>
                        <h2 className="journal-title" style={{ fontSize: '40px', marginBottom: '40px' }}>Commit</h2>

                        <div className="journal-line" style={{ marginBottom: '40px', maxWidth: '200px' }}>
                            <label style={{ fontSize: '16px', display: 'block', marginBottom: '8px', color: '#2c2416' }}>Date</label>
                            <input
                                type="date"
                                value={formData.commitDate}
                                onChange={(e) => handleChange('commitDate', e.target.value)}
                                disabled={isLocked}
                            />
                        </div>

                        <p className="handwritten-label" style={{
                            // fontSize: '22px',
                            marginBottom: '30px',
                            color: '#2c2416',
                            lineHeight: '1.6'
                        }}>
                            Below are the habits that I'll be keeping (and adding). In the next two weeks, I will...
                        </p>

                        <div className="journal-line multi-line" style={{ marginTop: '30px' }}>
                            <textarea
                                value={formData.commitHabits}
                                onChange={(e) => handleChange('commitHabits', e.target.value)}
                                placeholder=""
                                rows={16}
                                style={{ width: '100%', lineHeight: '32px' }}
                                disabled={isLocked}
                            />
                        </div>

                        <p className="journal-prompt" style={{ fontSize: '13px', lineHeight: '1.6', marginTop: '50px', color: '#3d3425' }}>
                            Only two weeks left! Finish strong!
                        </p>
                    </div>

                    {/* Section 3: My Progress */}
                    <div style={{ marginTop: '80px' }}>
                        <h2 className="journal-title" style={{ fontSize: '32px', marginBottom: '20px' }}>My progress</h2>
                        <p className="journal-prompt" style={{ fontSize: '16px', marginBottom: '40px' }}>How are things going?</p>

                        <div style={{ position: 'relative', overflowX: 'auto', margin: '0 -20px', padding: '0 20px' }}>
                            <table className="progress-calendar-table" style={{ minWidth: '320px' }}>
                                <tbody>
                                    {/* Week 1 Header */}
                                    <tr className="progress-calendar-row">
                                        {[1, 2, 3, 4, 5, 6, 7].map(day => (
                                            <td key={day} className="progress-calendar-header">
                                                Day {day}
                                            </td>
                                        ))}
                                    </tr>
                                    {/* Week 1 Moods */}
                                    <tr className="progress-calendar-row">
                                        {[1, 2, 3, 4, 5, 6, 7].map(day => (
                                            <td key={day} className="progress-calendar-cell">
                                                <div style={{ display: 'flex', justifyContent: 'center', gap: '5px', alignItems: 'center', flexWrap: 'wrap' }}>
                                                    {moodEmojis.map(emoji => (
                                                        <span
                                                            key={emoji}
                                                            onClick={(e) => handleMoodClick(day, emoji, e)}
                                                            style={{
                                                                cursor: 'pointer',
                                                                fontSize: '24px',
                                                                opacity: formData[`day${day}Mood`] === emoji ? 1 : 0.3,
                                                                transition: 'opacity 0.2s',
                                                                userSelect: 'none'
                                                            }}
                                                        >
                                                            {emoji}
                                                        </span>
                                                    ))}
                                                </div>
                                            </td>
                                        ))}
                                    </tr>
                                    {/* Week 2 Header */}
                                    <tr className="progress-calendar-row">
                                        {[8, 9, 10, 11, 12, 13, 14].map(day => (
                                            <td key={day} className="progress-calendar-header">
                                                Day {day}
                                            </td>
                                        ))}
                                    </tr>
                                    {/* Week 2 Moods */}
                                    <tr className="progress-calendar-row">
                                        {[8, 9, 10, 11, 12, 13, 14].map(day => (
                                            <td key={day} className="progress-calendar-cell">
                                                <div style={{ display: 'flex', justifyContent: 'center', gap: '5px', alignItems: 'center', flexWrap: 'wrap' }}>
                                                    {moodEmojis.map(emoji => (
                                                        <span
                                                            key={emoji}
                                                            onClick={(e) => handleMoodClick(day, emoji, e)}
                                                            style={{
                                                                cursor: 'pointer',
                                                                fontSize: '24px',
                                                                opacity: formData[`day${day}Mood`] === emoji ? 1 : 0.3,
                                                                transition: 'opacity 0.2s',
                                                                userSelect: 'none'
                                                            }}
                                                        >
                                                            {emoji}
                                                        </span>
                                                    ))}
                                                </div>
                                            </td>
                                        ))}
                                    </tr>
                                </tbody>
                            </table>

                            {/* Emoji Popup */}
                            {emojiPopup.show && (
                                <div
                                    style={{
                                        position: 'fixed',
                                        top: emojiPopup.position ? emojiPopup.position.y - 60 : '50%',
                                        left: emojiPopup.position ? emojiPopup.position.x - 40 : '50%',
                                        transform: 'translate(-50%, -50%)',
                                        backgroundColor: emojiPopup.emoji === '😊' ? '#FEF3C7' :
                                            emojiPopup.emoji === '😐' ? '#FED7AA' : '#FECACA',
                                        borderRadius: '50%',
                                        width: '80px',
                                        height: '80px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        fontSize: '40px',
                                        zIndex: 1000,
                                        boxShadow: '0 8px 25px rgba(0, 0, 0, 0.15)',
                                        animation: 'popupBounce 0.6s ease-out'
                                    }}
                                >
                                    {emojiPopup.emoji}
                                </div>
                            )}
                        </div>

                        {/* Notes Section */}
                        <div style={{ marginTop: '60px' }}>
                            <h2 className="journal-title" style={{ fontSize: '32px', marginBottom: '30px' }}>Notes</h2>
                            <div className="journal-line multi-line">
                                <textarea
                                    value={formData.progressNotes}
                                    onChange={(e) => handleChange('progressNotes', e.target.value)}
                                    placeholder=""
                                    rows={12}
                                    style={{ width: '100%', lineHeight: '32px' }}
                                    disabled={isFieldDisabled('progressNotes', isLocked)}
                                />
                            </div>
                        </div>
                    </div>

                    <JournalNavigation
                        prevPage="/journal/checkin-04"
                        nextPage="/journal/checkin-06"
                    />
                </div>
            </div>
        </>
    );
};

export default CheckIn05;
